import * as React from 'react'

import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import { twMerge } from 'tailwind-merge'

const badgeVariants = cva(
  'rounded-md border px-[6px] text-sm font-medium w-fit shrink-0 [&>svg]:size-3 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary-200 dark:bg-primary-800 text-primary-900 dark:text-primary-100 [a&]:hover:bg-primary-200/90 [a&]:hover:text-primary-900 [a&]:dark:hover:text-primary-100 [a&]:hover:border-primary-800/90',
        destructive:
          'border-transparent bg-red-600 dark:bg-red-600 text-white dark:text-white [a&]:hover:bg-red-600/90 focus-visible:ring-red-600/20 dark:focus-visible:ring-red-500/40',
        positive:
          'border-transparent bg-green-600 dark:bg-green-600/60 text-white dark:text-white [a&]:hover:bg-green-600/90 focus-visible:ring-green-600/20 dark:focus-visible:ring-green-500/40',
        outline:
          'bg-primary-50 dark:bg-primary-800 border-primary-200 dark:border-primary-700 text-primary-900 dark:text-primary-100 [a&]:hover:bg-primary-200/90 [a&]:hover:text-primary-900 [a&]:dark:hover:text-primary-100 [a&]:hover:border-primary-800/90',
        blue: 'border-transparent bg-blue-50 dark:bg-blue-700/40 text-blue-700 dark:text-blue-100 border-blue-200 dark:border-blue-800 [a&]:hover:bg-blue-50/90 [a&]:hover:text-blue-700 [a&]:dark:hover:text-blue-200 [a&]:hover:border-blue-800/90',
        purple:
          'border-transparent bg-purple-50 dark:bg-purple-700/40 text-purple-700 dark:text-purple-100 border-purple-200 dark:border-purple-800 [a&]:hover:bg-purple-50/90 [a&]:hover:text-purple-700 [a&]:dark:hover:text-purple-200 [a&]:hover:border-purple-800/90',
        violet:
          'border-transparent bg-violet-50 dark:bg-violet-700/40 text-violet-700 dark:text-violet-100 border-violet-200 dark:border-violet-800 [a&]:hover:bg-violet-50/90 [a&]:hover:text-violet-700 [a&]:dark:hover:text-violet-200 [a&]:hover:border-violet-800/90',
        amber:
          'border-transparent bg-amber-50 dark:bg-amber-700/40 text-amber-700 dark:text-amber-100 border-amber-200 dark:border-amber-800 [a&]:hover:bg-amber-50/90 [a&]:hover:text-amber-700 [a&]:dark:hover:text-amber-200 [a&]:hover:border-amber-800/90',
        green:
          'bg-green-50 dark:bg-green-700/40 text-green-700 dark:text-green-100 border-green-200 dark:border-green-800 [a&]:hover:bg-green-50/90 [a&]:hover:text-green-700 [a&]:dark:hover:text-green-200 [a&]:hover:border-green-800/90',
        indigo:
          'bg-indigo-50 dark:bg-indigo-700/40 text-indigo-700 dark:text-indigo-100 border-indigo-200 dark:border-indigo-800 [a&]:hover:bg-indigo-50/90 [a&]:hover:text-indigo-700 [a&]:dark:hover:text-indigo-200 [a&]:hover:border-indigo-800/90',
        red: 'bg-red-50 dark:bg-red-700/40 text-red-700 dark:text-red-100 border-red-200 dark:border-red-800 [a&]:hover:bg-red-50/90 [a&]:hover:text-red-700 [a&]:dark:hover:text-red-200 [a&]:hover:border-red-800/90',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

interface BadgeProps
  extends React.ComponentProps<'div'>, VariantProps<typeof badgeVariants> {
  asChild?: boolean
}

function Badge({ className, variant, asChild = false, ...props }: BadgeProps) {
  const Comp = asChild ? Slot : 'div'
  return (
    <Comp
      data-slot="badge"
      className={twMerge(badgeVariants({ variant }), className)}
      {...props}
    />
  )
}

function BetaBadge({ className, variant = 'amber', ...props }: BadgeProps) {
  return (
    <Badge
      variant={variant}
      className={twMerge('text-xs', className)}
      {...props}
    >
      Beta
    </Badge>
  )
}

export { Badge, BetaBadge, badgeVariants, type BadgeProps }
